import React, { useMemo } from 'react'

import { isNil } from 'lodash-es'

import {
  ProviderInsightsCompetencyAreaLabels,
  ProviderInsightsCompetencyCategory,
} from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/constants'
import { formatNumberToDecimalPlaces } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { CareInsightsMetrics } from '@lyrahealth-inc/shared-app-logic/src/models/careInsights/CareInsights'
import {
  CareInsightsSubsection,
  CareInsightsSubsectionProps,
} from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/careInsightsSubsection/CareInsightsSubsection'

interface TherapeuticRelationshipInsightsProps extends Partial<CareInsightsSubsectionProps> {
  metricInsights?: CareInsightsMetrics
}

export const TherapeuticRelationshipInsights: React.FC<TherapeuticRelationshipInsightsProps> = ({
  aiInsightsCurrentMonth,
  aiInsightsPastMonth,
  getClientInfoFromIds,
  isLoadingClients,
  isLoadingCareInsightsMetrics,
  isLoadingProviderInsights,
  isSupervisor,
  metricInsights,
  onClientPress,
  onSkillPress,
  onSupervisorUsageGuidelinesPress,
}) => {
  const therapeuticRelationshipMetrics = useMemo(() => {
    if (!metricInsights) {
      return []
    }
    const {
      therapeutic_alliance_score: therapeuticAllianceScore,
      therapeutic_alliance_program_average: therapeuticAllianceProgramAverage,
      therapeutic_alliance_max: therapeuticAllianceMax,
      dropout_rate: dropoutRate,
      dropout_rate_program_average: dropoutRateProgramAverage,
    } = metricInsights
    return [
      {
        title: 'Therapeutic alliance',
        value:
          isNil(therapeuticAllianceScore) || isNil(therapeuticAllianceMax)
            ? undefined
            : formatNumberToDecimalPlaces({ value: therapeuticAllianceScore, isPercentage: false }) +
              '/' +
              therapeuticAllianceMax,
        programAverage: isNil(therapeuticAllianceProgramAverage)
          ? undefined
          : formatNumberToDecimalPlaces({ value: therapeuticAllianceProgramAverage, isPercentage: false }),
        testID: 'TherapeuticAlliance',
      },
      {
        title: 'Dropouts',
        value: isNil(dropoutRate) ? undefined : formatNumberToDecimalPlaces({ value: dropoutRate, isPercentage: true }),
        programAverage: isNil(dropoutRateProgramAverage)
          ? undefined
          : formatNumberToDecimalPlaces({ value: dropoutRateProgramAverage, isPercentage: true }),
        testID: 'Dropouts',
        tooltipContent: '% of all episodes closed as dropout',
      },
    ]
  }, [metricInsights])
  return (
    <CareInsightsSubsection
      title={ProviderInsightsCompetencyAreaLabels[ProviderInsightsCompetencyCategory.ATTUNEMENT_AND_RAPPORT]}
      competencyAreaName={ProviderInsightsCompetencyCategory.ATTUNEMENT_AND_RAPPORT}
      testIDPrefix='TherapeuticRelationshipInsights'
      aiInsightsCurrentMonth={aiInsightsCurrentMonth}
      aiInsightsPastMonth={aiInsightsPastMonth}
      getClientInfoFromIds={getClientInfoFromIds}
      isLoadingClients={isLoadingClients}
      isLoadingCareInsightsMetrics={isLoadingCareInsightsMetrics}
      isLoadingProviderInsights={isLoadingProviderInsights}
      metrics={therapeuticRelationshipMetrics}
      metricsPerRow={2}
      isSupervisor={isSupervisor}
      onClientPress={onClientPress}
      onSkillPress={onSkillPress}
      onSupervisorUsageGuidelinesPress={onSupervisorUsageGuidelinesPress}
    />
  )
}
